import React, { useRef, useState } from 'react'
import { OfferBlock, OfferBlockAttachments } from '../../models/shared/Offer'
import BlockToolbar from './BlockToolbar'
import { useText } from '../../context/TextContext'
import { Deal } from '../../models/shared/Deal'
import { Attachement } from '../../models/shared/Attachment'
import { AdvancedButton } from '../AdvancedButton'
import DealModal from '../modals/DealModal'
import AttachmentPreview from '../AttachmentPreview'

interface Props {
    block: OfferBlock;
    deal: Deal;
    readOnly?: boolean;
    onBlockUpdate: (updatedBlock: OfferBlock) => void;
    onBlockDelete: () => void;
    onMoveUp?: () => void;
    onMoveDown?: () => void;
}

export default function BlockAttachments(props: Props) {
    const { block: generalBlock, deal, onBlockUpdate, onBlockDelete } = props;
    const block = generalBlock as OfferBlockAttachments;
    const [showToolbar, setShowToolbar] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const text = useText();
    const [showDeal, setShowDeal] = React.useState(false)
    const [previewIndex, setPreviewIndex] = useState<number>(-1)
    const [isPreviewOpen, setIsPreviewOpen] = useState(false)
    const attachments = deal.attachments || [];

    const handleAttachmentToggle = (attachment: Attachement) => {
        const selectedAttachments = block.selectedAttachments || [];
        let newSelectedAttachments: string[];

        if (selectedAttachments.includes(attachment.path)) {
            // Remove attachment from selection
            newSelectedAttachments = selectedAttachments.filter(path => path !== attachment.path);
        } else {
            // Add attachment to selection
            newSelectedAttachments = [...selectedAttachments, attachment.path];
        }

        const blockUpdate: OfferBlockAttachments = {
            ...block,
            selectedAttachments: newSelectedAttachments
        };

        onBlockUpdate(blockUpdate);
    };

    const selectedAttachments = block.selectedAttachments || [];
    const visibleAttachments = attachments.filter(attachment => selectedAttachments.includes(attachment.path));

    if (props.readOnly && !visibleAttachments.length) {
        return null;
    }

    const handlePreview = (index: number) => {
        setPreviewIndex(index)
        setIsPreviewOpen(true)
    }

    const handleClosePreview = () => {
        setIsPreviewOpen(false)
        setPreviewIndex(-1)
    }

    function renderAttachments() {

        if (!attachments.length) {
            return <div className='center p-5 w-100'
                onClick={() => !props.readOnly && setShowToolbar(true)}
            >
                {text.noAttachments}
            </div>
        }

        return <div className="d-flex overflow-auto gap-3 py-2">
            {showedAttachements.map((attachment, index) => {
                const isSelected = selectedAttachments.includes(attachment.path);

                if (props.readOnly) {
                    return <div key={index} className="rounded shadow-sm block-attachment">
                        <div
                            className="w-100 h-100 center overflow-hidden"
                            onClick={() => handlePreview(index)}
                            style={{ cursor: 'pointer' }}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    handlePreview(index)
                                }
                            }}
                        >
                            {renderAttachmentContent(attachment)}
                        </div>
                    </div>
                }

                return <div key={index} className="rounded shadow-sm block-attachment position-relative">
                    <AdvancedButton
                        className='position-absolute top-0 end-0 m-1 z-1'
                        variant={isSelected ? 'secondary' : 'secondary'}
                        icon={isSelected ? 'bi-eye-fill' : 'bi-eye-slash-fill'}
                        onClick={() => handleAttachmentToggle(attachment)}
                        size='sm'
                    />

                    <div className="w-100 h-100 center overflow-hidden animate"
                        style={{ filter: isSelected ? 'grayscale(0%)' : 'grayscale(100%)' }}
                    >
                        {renderAttachmentContent(attachment)}
                    </div>
                </div>


            })}
        </div>

    }


    function renderAttachmentContent(attachment: Attachement) {
        return attachment.fileType?.startsWith('image') && attachment.url !== '/' ? (
            <img
                className="rounded w-100 h-100"
                src={attachment.url}
                alt={attachment.name}
                style={{ objectFit: 'cover' }}
            />
        ) : (
            <div className='center flex-column h-100 w-100'>
                <i className="bi bi-file-earmark-text fs-1 text-primary" />
                <div className='small'>{attachment.name}</div>
            </div>
        );
    }


    const showedAttachements = props.readOnly ? visibleAttachments : attachments;
    return (
        <>
            <div
                ref={contentRef}
                className="position-relative w-100"
                onClick={() => !props.readOnly && setShowToolbar(true)}
            >
                <BlockToolbar
                    contentRef={contentRef}
                    onDelete={onBlockDelete}
                    onMoveUp={props.onMoveUp}
                    onMoveDown={props.onMoveDown}
                    showToolbar={showToolbar}
                    setShowToolbar={setShowToolbar}
                    readOnly={props.readOnly}
                >
                    <AdvancedButton
                        variant="clean"
                        size="sm"
                        icon='bi-images'
                        onClick={() => setShowDeal(true)}
                    >
                        {text.edit}
                    </AdvancedButton>
                </BlockToolbar>

                {renderAttachments()}

                {showDeal && <DealModal deal={deal} onClose={() => setShowDeal(false)} />}
            </div>

            {props.readOnly && (
                <AttachmentPreview
                    attachments={visibleAttachments}
                    initialIndex={previewIndex}
                    isOpen={isPreviewOpen}
                    onClose={handleClosePreview}
                />
            )}
        </>
    );
}
